import type { Document, Organization } from "~/types/collections";

export type GatedFeature = "download" | "sign";

/**
 * Returns true if the user has access to this document for the given feature.
 * - Subscribers and beta users always have access.
 * - If no document is provided, returns true (falls through to subscription-only gating).
 * - Download: requires paymentStatus "purchased", "exempt", or "free".
 * - Sign: requires paymentStatus "purchased" only.
 * - undefined/null paymentStatus: no access.
 */
export const hasFeatureAccess = (
  organization: Organization | undefined,
  document: Document | undefined,
  isBetaUser: boolean,
  feature?: GatedFeature
): boolean => {
  if (organization?.subscription?.grantsAccess || isBetaUser) return true;
  if (!document) return true;
  if (!document.paymentStatus) return false;

  const status = document.paymentStatus;

  if (feature === "sign") {
    return status === "purchased";
  }

  return status === "purchased" || status === "exempt" || status === "free";
};

/**
 * Returns true if this document requires purchase for the given feature.
 */
export const featureNeedsPurchase = (
  organization: Organization | undefined,
  document: Document | undefined,
  isBetaUser: boolean,
  feature?: GatedFeature
): boolean => !hasFeatureAccess(organization, document, isBetaUser, feature);

/**
 * @deprecated Use hasFeatureAccess instead
 */
export const hasDocumentAccess = (
  organization: Organization | undefined,
  document: Document | undefined,
  isBetaUser: boolean
): boolean => hasFeatureAccess(organization, document, isBetaUser);

/**
 * @deprecated Use featureNeedsPurchase instead
 */
export const documentNeedsPurchase = (
  organization: Organization | undefined,
  document: Document | undefined,
  isBetaUser: boolean
): boolean => featureNeedsPurchase(organization, document, isBetaUser);
